/**
 * Payment Instruction Button Visibility and Content Selection Logic
 * 
 * Business Rules:
 * 1. If pdaId is NULL (FDA only):
 *    - Button visible when FDA.status = COMPLETED
 *    - Use FDA content
 * 
 * 2. If pdaId is NOT NULL (FDA linked with PDA):
 *    - Button visible when PDA.status = COMPLETED
 *    - Use PDA content until FDA.status = COMPLETED
 *    - Switch to FDA content once FDA.status = COMPLETED
 */

export interface PaymentInstructionState {
  isButtonVisible: boolean;
  contentType: 'FDA' | 'PDA';
  amount: number;
}

export class PaymentInstructionLogic {
  /**
   * Determines if the Payment Instruction button should be visible
   * @param pdaId - PDA ID (null if FDA only)
   * @param pdaStatus - PDA status
   * @param fdaStatus - FDA status
   * @returns true if button should be visible
   */
  static isButtonVisible(pdaId: any, pdaStatus: string, fdaStatus: string): boolean {
    if (!pdaId) {
      return fdaStatus?.toUpperCase() === 'COMPLETED';
    }
    return pdaStatus?.toUpperCase() === 'COMPLETED';
  }

  /**
   * Determines which content source to use and the amount
   * @param pdaId - PDA ID (null if FDA only)
   * @param fdaStatus - FDA status
   * @param pdaAmount - PDA amount
   * @param fdaAmount - FDA amount
   * @returns object with content type and amount
   */
  static getContentSource(
    pdaId: any,
    fdaStatus: string,
    pdaAmount: number,
    fdaAmount: number
  ): { type: 'FDA' | 'PDA'; amount: number } {
    if (!pdaId) {
      return { type: 'FDA', amount: fdaAmount };
    }

    if (fdaStatus?.toUpperCase() === 'COMPLETED') {
      return { type: 'FDA', amount: fdaAmount };
    }

    return { type: 'PDA', amount: pdaAmount };
  }

  /**
   * Complete state determination
   */
  static getPaymentInstructionState(
    pdaId: any,
    pdaStatus: string,
    fdaStatus: string,
    pdaAmount: number,
    fdaAmount: number
  ): PaymentInstructionState {
    return {
      isButtonVisible: this.isButtonVisible(pdaId, pdaStatus, fdaStatus),
      contentType: this.getContentSource(pdaId, fdaStatus, pdaAmount, fdaAmount).type,
      amount: this.getContentSource(pdaId, fdaStatus, pdaAmount, fdaAmount).amount
    };
  }
}
